import {
    FETCH_KPI_REQUEST,
    FETCH_KPI_SUCCESS,
    FETCH_KPI_FAILURE,
    FETCH_CHART_REQUEST,
    FETCH_CHART_SUCCESS,
    FETCH_CHART_FAILURE,
    UPDATE_DASHBOARD_FILTERS,
    CLEAR_DASHBOARD_DATA,
    FETCH_AGGREGATED_REQUEST,
    FETCH_AGGREGATED_SUCCESS,
    FETCH_AGGREGATED_FAILURE,
    FETCH_COMPANIES_REQUEST,
    FETCH_COMPANIES_SUCCESS,
    FETCH_COMPANIES_FAILURE,
    FETCH_SELLERS_REQUEST,
    FETCH_SELLERS_SUCCESS,
    FETCH_SELLERS_FAILURE,
    SAVE_PREFERENCE_REQUEST,
    SAVE_PREFERENCE_SUCCESS,
    SAVE_PREFERENCE_FAILURE,
    LOAD_PREFERENCE_REQUEST,
    LOAD_PREFERENCE_SUCCESS,
    LOAD_PREFERENCE_FAILURE,
    FETCH_ALL_PREFERENCES_SUCCESS,
    DELETE_PREFERENCE_SUCCESS
} from '../actions/dashboardAnalytics.actions';

const initialState = {
    kpis: {
        totalLabels: 0,
        outboundOrders: 0,
        totalSKUs: 0,
        inboundConsignments: 0,
        openTasks: 0,
        walletBalance: 0,
        labelsChangePercentage: 0,
        ordersChangePercentage: 0,
        skUsChangePercentage: 0,
        inboundChangePercentage: 0,
        tasksChangePercentage: 0
    },
    widgets: {
        // Existing 6 widgets
        dailyShipments: null,
        dailyOrders: null,
        carrierDistribution: null,
        activeShipmentsStatus: null,
        tasksByType: null,
        
        // Phase 1 (9 additional)
        monthlyRevenueTrend: null,
        shippingServiceBreakdown: null,
        orderStatusDistribution: null,
        revenueByMarketplace: null,
        dailyConsignments: null,
        inboundByConsignmentType: null,
        tasksByPriority: null,
        carrierConnectsByCarrier: null,
        carrierConnectsByBillingStatus: null,
        
        // Phase 2 - Shipment Performance (4)
        averageShippingCost: null,
        shipmentsByWeightClass: null,
        topShippingDestinations: null,
        carrierPerformanceScore: null,
        
        // Phase 2 - Order Analytics (4)
        hourlyShipments: null,
        averageOrderValue: null,
        ordersByProductCategory: null,
        orderCancellationRate: null,
        
        // Phase 2 - Inventory Insights (4)
        topProducts: null,
        productDiversity: null,
        lowStockCount: null,
        inventoryValueByCategory: null,
        weeklyOrderComparison: null,
        
        // Phase 2 - Operational Efficiency (4)
        taskCompletionRate: null,
        averageTaskDuration: null,
        consignmentProcessingTime: null,
        peakShippingHours: null,
        
        // Phase 3 - Advanced Analytics (10)
        shippingCostVarianceAnalysis: null,
        carrierReliabilityScore: null,
        shipmentRouteEfficiencyMatrix: null,
        orderFulfillmentCycleTime: null,
        customerRepeatOrderRate: null,
        orderValueDistribution: null,
        skuProfitabilityIndex: null,
        inventoryTurnoverVelocity: null,
        multiChannelPerformanceScorecard: null,
        peakHourCapacityUtilization: null,
        // Returns & Refunds (7)
        returnPackagesCount: null,
        returnPackagesByType: null,
        returnProductsByStatus: null,
        returnBillingStatus: null,
        refundRequestStatus: null,
        returnsPendingCount: null,
        refundVolumeByCompany: null
    },
    metadata: {
        totalWidgets: 41,
        successfulWidgets: 0,
        failedWidgets: 0,
        executionTimeMs: 0,
        generatedAtUtc: 0,
        errors: {}
    },
    filters: {
        fromDate: null,
        toDate: null,
        companyId: null,
        sellerId: null,
        period: 'This Month'
    },
    companies: [],
    sellers: [],
    companiesLoading: false,
    companiesError: null,
    sellersLoading: false,
    sellersError: null,
    loading: false,
    error: null,
    lastUpdated: null,
    // Dashboard Preference
    preference: {
        saving: false,
        loading: false,
        saved: null,        // currently active saved preset
        allPresets: [],     // all presets for this user
        error: null
    }
};

export const dashboardAnalyticsReducer = (state = initialState, action) => {
    switch (action.type) {
        // Legacy individual fetch actions (keep for backward compatibility)
        case FETCH_KPI_REQUEST:
        case FETCH_AGGREGATED_REQUEST:
            return {
                ...state,
                loading: true,
                error: null
            };

        case FETCH_KPI_SUCCESS:
            return {
                ...state,
                kpis: action.payload,
                loading: false,
                lastUpdated: new Date().toISOString()
            };

        case FETCH_KPI_FAILURE:
        case FETCH_AGGREGATED_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload
            };

        case FETCH_CHART_REQUEST:
        case FETCH_CHART_SUCCESS:
        case FETCH_CHART_FAILURE:
            // Legacy chart actions - retained for compatibility
            return state;

        // NEW: Aggregated dashboard actions
        case FETCH_AGGREGATED_SUCCESS:
            return {
                ...state,
                kpis: action.payload.kpis,
                widgets: { ...state.widgets, ...action.payload.widgets },
                metadata: action.payload.metadata,
                loading: false,
                error: null,
                lastUpdated: new Date().toISOString()
            };

        case UPDATE_DASHBOARD_FILTERS:
            return {
                ...state,
                filters: { ...state.filters, ...action.payload }
            };

        case CLEAR_DASHBOARD_DATA:
            return initialState;

        // NEW: Company and Seller Dropdown Actions
        case FETCH_COMPANIES_REQUEST:
            return {
                ...state,
                companiesLoading: true,
                companiesError: null
            };

        case FETCH_COMPANIES_SUCCESS:
            return {
                ...state,
                companies: action.payload,
                companiesLoading: false,
                companiesError: null
            };

        case FETCH_COMPANIES_FAILURE:
            return {
                ...state,
                companiesLoading: false,
                companiesError: action.payload
            };

        case FETCH_SELLERS_REQUEST:
            return {
                ...state,
                sellersLoading: true,
                sellersError: null
            };

        case FETCH_SELLERS_SUCCESS:
            return {
                ...state,
                sellers: action.payload,
                sellersLoading: false,
                sellersError: null
            };

        case FETCH_SELLERS_FAILURE:
            return {
                ...state,
                sellersLoading: false,
                sellersError: action.payload
            };

        // ─── Dashboard Preference ────────────────────────────────────────────
        case SAVE_PREFERENCE_REQUEST:
            return {
                ...state,
                preference: { ...state.preference, saving: true, error: null }
            };

        case SAVE_PREFERENCE_SUCCESS:
            return {
                ...state,
                preference: {
                    ...state.preference,
                    saving: false,
                    saved: action.payload,
                    allPresets: state.preference.allPresets.some(p => p.Id === action.payload.Id)
                        ? state.preference.allPresets.map(p => p.Id === action.payload.Id ? action.payload : p)
                        : [...state.preference.allPresets, action.payload]
                }
            };

        case SAVE_PREFERENCE_FAILURE:
            return {
                ...state,
                preference: { ...state.preference, saving: false, error: action.payload }
            };

        case LOAD_PREFERENCE_REQUEST:
            return {
                ...state,
                preference: { ...state.preference, loading: true, error: null }
            };

        case LOAD_PREFERENCE_SUCCESS:
            return {
                ...state,
                filters: action.payload.filters,
                preference: {
                    ...state.preference,
                    loading: false,
                    saved: action.payload.preference
                }
            };

        case LOAD_PREFERENCE_FAILURE:
            return {
                ...state,
                preference: { ...state.preference, loading: false }
            };

        case FETCH_ALL_PREFERENCES_SUCCESS:
            return {
                ...state,
                preference: { ...state.preference, allPresets: action.payload }
            };

        case DELETE_PREFERENCE_SUCCESS:
            return {
                ...state,
                preference: {
                    ...state.preference,
                    allPresets: state.preference.allPresets.filter(p => p.Id !== action.payload),
                    saved: state.preference.saved?.Id === action.payload ? null : state.preference.saved
                }
            };

        default:
            return state;
    }
};
